import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  Badge,
  Divider,
  Tooltip,
  Card,
  CardContent,
  CardActions
} from '@mui/material'
import {
  Notifications as NotificationsIcon,
  Comment as CommentIcon,
  CheckCircle,
  Circle,
  Delete,
  Reply,
  OpenInNew,
  DoneAll,
  FilterList
} from '@mui/icons-material'
import axios from 'axios'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

const NotificacionesView = () => {
  const navigate = useNavigate()
  const [notificaciones, setNotificaciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [tabValue, setTabValue] = useState(0)
  const [selectedNotificacion, setSelectedNotificacion] = useState(null)
  const [openRespondDialog, setOpenRespondDialog] = useState(false)
  const [respuesta, setRespuesta] = useState('')
  const [stats, setStats] = useState({ total: 0, no_leidas: 0, no_resueltas: 0 })

  useEffect(() => {
    loadNotificaciones()
    loadStats()
  }, [tabValue])

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/notificaciones/stats', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStats(response.data)
    } catch (error) {
      console.error('Error al cargar estadísticas:', error)
    }
  }

  const loadNotificaciones = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')

      let params = {}
      if (tabValue === 1) {
        params.leida = false
      } else if (tabValue === 2) {
        params.resuelta = false
      }

      const response = await axios.get('/api/notificaciones', {
        headers: { Authorization: `Bearer ${token}` },
        params
      })

      setNotificaciones(response.data)
    } catch (error) {
      setError('Error al cargar notificaciones: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleMarcarLeida = async (notificacionId) => {
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.post(`/api/notificaciones/${notificacionId}/marcar-leida`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      loadNotificaciones()
      loadStats()
    } catch (error) {
      setError('Error al marcar como leída: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleMarcarTodasLeidas = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.post('/api/notificaciones/marcar-todas-leidas', {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Todas las notificaciones marcadas como leídas')
      loadNotificaciones()
      loadStats()
    } catch (error) {
      setError('Error al marcar todas como leídas: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleEliminar = async (notificacionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta notificación?')) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/notificaciones/${notificacionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Notificación eliminada')
      loadNotificaciones()
      loadStats()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleResponder = (notificacion) => {
    setSelectedNotificacion(notificacion)
    setRespuesta('')
    setOpenRespondDialog(true)
  }

  const handleEnviarRespuesta = async () => {
    if (!respuesta.trim()) {
      setError('La respuesta no puede estar vacía')
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.put(`/api/notificaciones/${selectedNotificacion.id}`, {
        respuesta: respuesta,
        resuelta: true,
        leida: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Respuesta enviada y notificación resuelta')
      setOpenRespondDialog(false)
      setSelectedNotificacion(null)
      setRespuesta('')
      loadNotificaciones()
      loadStats()
    } catch (error) {
      setError('Error al enviar respuesta: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleMarcarResuelta = async (notificacionId) => {
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.put(`/api/notificaciones/${notificacionId}`, {
        resuelta: true,
        leida: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Notificación marcada como resuelta')
      loadNotificaciones()
      loadStats()
    } catch (error) {
      setError('Error al marcar como resuelta: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleIrA = (notificacion) => {
    // Marcar como leída al navegar
    handleMarcarLeida(notificacion.id)

    if (notificacion.url_destino) {
      navigate(notificacion.url_destino)
    }
  }

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'comentario':
        return 'primary'
      case 'solicitud_cambio':
        return 'warning'
      case 'aprobacion_requerida':
        return 'error'
      default:
        return 'default'
    }
  }

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'comentario':
        return <CommentIcon />
      default:
        return <NotificationsIcon />
    }
  }

  const formatFecha = (fecha) => {
    if (!fecha) return ''
    try {
      return format(new Date(fecha), "d 'de' MMMM 'a las' HH:mm", { locale: es })
    } catch (error) {
      return fecha
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando notificaciones...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NotificationsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Notificaciones
          </Typography>
        </Box>
        {stats.no_leidas > 0 && (
          <Button
            variant="outlined"
            startIcon={<DoneAll />}
            onClick={handleMarcarTodasLeidas}
          >
            Marcar todas como leídas
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab
            label={
              <Badge badgeContent={stats.total} color="primary">
                Todas
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={stats.no_leidas} color="error">
                No leídas
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={stats.no_resueltas} color="warning">
                Sin resolver
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {notificaciones.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No hay notificaciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cuando recibas comentarios o solicitudes, aparecerán aquí
          </Typography>
        </Paper>
      ) : (
        <List>
          {notificaciones.map((notificacion) => (
            <Card
              key={notificacion.id}
              sx={{
                mb: 2,
                bgcolor: notificacion.leida ? 'background.paper' : 'action.hover',
                borderLeft: `4px solid`,
                borderColor: notificacion.resuelta ? 'success.main' : getTipoColor(notificacion.tipo) + '.main'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <Box sx={{ color: getTipoColor(notificacion.tipo) + '.main' }}>
                    {getTipoIcon(notificacion.tipo)}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">
                        {notificacion.titulo}
                      </Typography>
                      <Chip
                        label={notificacion.tipo.replace('_', ' ')}
                        color={getTipoColor(notificacion.tipo)}
                        size="small"
                      />
                      {!notificacion.leida && (
                        <Chip label="Nueva" color="error" size="small" />
                      )}
                      {notificacion.resuelta && (
                        <Chip label="Resuelta" color="success" size="small" />
                      )}
                    </Box>

                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {notificacion.mensaje}
                    </Typography>

                    {notificacion.respuesta && (
                      <Paper sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          Respuesta:
                        </Typography>
                        <Typography variant="body2">
                          {notificacion.respuesta}
                        </Typography>
                        {notificacion.respondida_en && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Respondida el {formatFecha(notificacion.respondida_en)}
                          </Typography>
                        )}
                      </Paper>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {formatFecha(notificacion.created_at)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {notificacion.url_destino && (
                    <Tooltip title="Ir al elemento">
                      <Button
                        size="small"
                        startIcon={<OpenInNew />}
                        onClick={() => handleIrA(notificacion)}
                      >
                        Ver
                      </Button>
                    </Tooltip>
                  )}

                  {!notificacion.resuelta && (
                    <>
                      <Button
                        size="small"
                        startIcon={<Reply />}
                        onClick={() => handleResponder(notificacion)}
                      >
                        Responder
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CheckCircle />}
                        color="success"
                        onClick={() => handleMarcarResuelta(notificacion.id)}
                      >
                        Resolver
                      </Button>
                    </>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!notificacion.leida && (
                    <Tooltip title="Marcar como leída">
                      <IconButton
                        size="small"
                        onClick={() => handleMarcarLeida(notificacion.id)}
                      >
                        <Circle fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Eliminar">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleEliminar(notificacion.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardActions>
            </Card>
          ))}
        </List>
      )}

      {/* Diálogo para responder */}
      <Dialog open={openRespondDialog} onClose={() => setOpenRespondDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Responder Notificación</DialogTitle>
        <DialogContent>
          {selectedNotificacion && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Mensaje original:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedNotificacion.mensaje}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Tu respuesta"
            value={respuesta}
            onChange={(e) => setRespuesta(e.target.value)}
            placeholder="Escribe tu respuesta aquí..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRespondDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEnviarRespuesta}>
            Enviar y Resolver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default NotificacionesView
